'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { Check, ChevronRight } from 'lucide-react'
import { Button, Badge } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'

/**
 * Agent definition for FeaturesAgentCards
 */
export interface FeaturesAgentCardsAgent {
  /** Agent name */
  name: string
  /** Agent avatar image */
  image: { src: string; alt: string }
  /** Agent role - displayed as a badge (e.g., "Product", "Engineering") */
  role: string
  /** Agent description */
  description: string
  /** Optional learn more link */
  link?: {
    text: string
    url: string
  }
}

export interface FeaturesAgentCardsHighlight {
  /** Highlight text - supports Markdown (bold, code, links) */
  text: string
}

/**
 * Props for FeaturesAgentCards component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - This component displays AI agent profiles in scrolling cards
 * - Each agent needs an avatar image (circular, 48-64px)
 * - The role prop becomes a colored badge above the description
 * - Best for 4-6 agents to balance the scrolling area with sticky content
 * - Avatar images should be provided at `/avatars/{Name}.png` path
 */
export interface FeaturesAgentCardsProps {
  /** Badge text displayed above heading */
  badge?: string
  /** Section heading */
  heading: string
  /** Section description */
  description?: string
  /** Highlight items with checkmarks on sticky side. Supports Markdown (bold, code, links). */
  highlights?: FeaturesAgentCardsHighlight[]
  /** Agent profile cards */
  agents: FeaturesAgentCardsAgent[]
  /** Optional CTA button on the left side */
  cta?: {
    text: string
    url: string
  }
  /** Additional class names */
  className?: string
}

/**
 * FeaturesAgentCards - Sticky header with scrolling AI agent profile cards
 *
 * **Layout:**
 * ```
 * ┌─────────────────────┬──────────────────────┐
 * │                     │   ┌──────────────┐   │
 * │  Badge              │   │   Avatar     │   │
 * │  Heading            │   │    Name      │   │
 * │  Description        │   │   [Badge]    │   │
 * │                     │   │ Description  │   │
 * │  [sticky on lg]     │   │  [Link →]    │   │
 * │                     │   └──────────────┘   │
 * │  ✓ Highlight 1      │                      │
 * │  ✓ Highlight 2      │   ┌──────────────┐   │
 * │  ✓ Highlight 3      │   │   Avatar     │   │
 * │                     │   │    ...       │   │
 * │                     │   └──────────────┘   │
 * └─────────────────────┴──────────────────────┘
 * ```
 *
 * **Best Use Cases:**
 * - AI agent team showcase pages
 * - Staff/team directory with profile cards
 * - Expert profiles with specializations
 *
 * @example
 * ```tsx
 * <FeaturesAgentCards
 *   badge="The Team"
 *   heading="Meet Your AI Agents"
 *   description="Six specialized agents, full stack coverage."
 *   highlights={[
 *     { text: 'Real GitHub identities' },
 *     { text: 'Natural language API' },
 *   ]}
 *   agents={[
 *     {
 *       name: 'Priya',
 *       image: { src: '/avatars/Priya.png', alt: 'Priya' },
 *       role: 'Product',
 *       description: 'Specs, roadmaps, priorities.',
 *       link: { text: 'Learn more', url: '/agents/priya' },
 *     },
 *   ]}
 * />
 * ```
 */
const FeaturesAgentCards = ({
  badge,
  heading,
  description,
  highlights = [],
  agents,
  cta,
  className,
}: FeaturesAgentCardsProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 sm:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <div className="relative grid gap-10 lg:grid-cols-2">
          {/* Left side - sticky on lg screens */}
          <motion.div
            className="top-32 mx-auto flex h-fit max-w-3xl flex-col items-center gap-4 lg:sticky lg:items-start"
            variants={headerAnimation}
          >
            {badge && (
              <Badge
                variant="outline"
                className="px-2.5 py-1.5 text-sm"
              >
                {badge}
              </Badge>
            )}
            <h2 className="text-center text-3xl font-semibold lg:text-left lg:text-4xl">
              {heading}
            </h2>
            {description && (
              <div className="text-center lg:text-left">
                <Markdown size="base">{description}</Markdown>
              </div>
            )}
            {highlights.length > 0 && (
              <div className="mt-6 flex flex-col gap-4">
                {highlights.map((highlight) => (
                  <div
                    key={highlight.text}
                    className="flex items-start gap-2"
                  >
                    <Check className="mt-1 size-4 shrink-0 text-primary" />
                    <div className="prose prose-sm max-w-none text-muted-foreground prose-p:my-0 prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                      <ReactMarkdown>{highlight.text}</ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {cta && (
              <Button asChild className="mt-6 w-full sm:w-auto">
                <a href={cta.url}>
                  {cta.text}
                  <ChevronRight className="ml-1 size-4" />
                </a>
              </Button>
            )}
          </motion.div>

          {/* Right side - scrolling agent cards */}
          <motion.div
            className="flex flex-col gap-4"
            variants={gridContainerVariants(0.1, 0.2)}
          >
            {agents.map((agent) => (
              <motion.div
                key={agent.name}
                className="flex flex-col items-center gap-4 rounded-[var(--radius)] border border-border p-7 lg:items-start"
                variants={itemAnimation}
              >
                {/* Avatar */}
                <div className="relative size-16 overflow-hidden rounded-full bg-muted">
                  <img
                    src={agent.image.src}
                    alt={agent.image.alt}
                    className="size-full object-cover"
                  />
                </div>

                {/* Name + Role Badge - same line on lg */}
                <div className="flex flex-col items-center gap-2 lg:flex-row lg:items-center">
                  <h3 className="text-center text-lg font-semibold lg:text-left">
                    {agent.name}
                  </h3>
                  <Badge variant="secondary" className="text-xs">
                    {agent.role}
                  </Badge>
                </div>

                {/* Description */}
                <p className="text-center text-sm text-muted-foreground md:text-base lg:text-left">
                  {agent.description}
                </p>

                {/* Link */}
                {agent.link && (
                  <Button
                    variant="ghost"
                    asChild
                    className="mt-2 w-full self-center sm:w-fit lg:self-start"
                  >
                    <a href={agent.link.url}>
                      {agent.link.text}
                      <ChevronRight className="ml-1 size-4" />
                    </a>
                  </Button>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { FeaturesAgentCards }
